import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useUser } from '../contexts/UserContext';
import { subscriptionService } from '../services/subscriptionService';

interface AIGeneratorSelectorModalProps {
  onClose: () => void;
  onSelectQuestGenerator: () => void;
  onSelectProgramGenerator: () => void;
}

const AIGeneratorSelectorModal: React.FC<AIGeneratorSelectorModalProps> = ({
  onClose,
  onSelectQuestGenerator,
  onSelectProgramGenerator,
}) => {
  const { t } = useLanguage();
  const { profile, showNotification, isDemoMode } = useUser();
  
  // Check if user can use AI features
  const canUseQuestGenerator = !isDemoMode && profile ? subscriptionService.canUseAIQuestGeneration(profile).allowed : false;
  const canUseProgramGenerator = !isDemoMode && profile ? subscriptionService.canUseAIProgramGeneration(profile).allowed : false;

  return (
    <div className="fixed inset-0 backdrop-blur-xl flex items-center justify-center z-50 p-4 modal-backdrop animate-fade-in">
      <div className="relative max-w-4xl w-full animate-scale-in">
        {/* Main Modal */}
        <div className="relative bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-md border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* Header - Fixed at top */}
          <div className="flex items-center justify-between p-6 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm border-b border-purple-500/20"
            style={{
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div>
              <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                <div className="w-2 h-2 bg-system-blue rounded-full animate-pulse"></div>
                <span className="text-xs sm:text-sm font-orbitron text-system-blue tracking-wider uppercase">
                  ［ AI System ］
                </span>
              </div>
              <h2 className="text-lg sm:text-2xl font-bold font-orbitron text-white tracking-wider">
                {t.aiSelector.title.toUpperCase()}
              </h2>
              <p className="text-sm text-gray-400 font-rajdhani">
                {t.aiSelector.subtitle}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-xl leading-none transition-all bg-gradient-to-b from-slate-700/50 to-slate-800/50 rounded-full p-2 border-t-2 border-l-2 border-slate-600/40 border-r-2 border-b-2 border-slate-700/60"
              style={{
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
              }}
            >
              ✕
            </button>
          </div>

        {/* Options Grid */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 overflow-y-auto max-h-[calc(85vh-140px)]">
          {/* Quest Generator Option */}
          <button
            onClick={() => {
              if (isDemoMode) {
                showNotification('🔒 Sign up to unlock AI quest generation and save your progress!', 'warning');
                return;
              }
              if (canUseQuestGenerator) {
                onSelectQuestGenerator();
              } else {
                const limitCheck = subscriptionService.canUseAIQuestGeneration(profile!);
                showNotification(
                  limitCheck.reason || 'You\'ve reached your AI quest generation limit',
                  'warning'
                );
              }
            }}
            disabled={!canUseQuestGenerator}
            className={`group relative overflow-hidden bg-gradient-to-b from-purple-950/40 via-slate-950/30 to-purple-950/40 backdrop-blur-sm border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-3xl p-6 text-left transition-all duration-300 ${
              canUseQuestGenerator
                ? 'hover:scale-[1.02] cursor-pointer hover:shadow-lg hover:shadow-purple-500/30'
                : 'opacity-60 cursor-not-allowed'
            }`}
            style={{
              boxShadow: canUseQuestGenerator ? '0 4px 12px rgba(168, 85, 247, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)' : '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
            }}
          >
            {!canUseQuestGenerator && (
              <div className="absolute top-2 right-2 bg-red-500/90 text-white px-3 py-1 rounded-lg text-xs font-bold font-orbitron z-10">
                🔒 LIMIT REACHED
              </div>
            )}
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="/icons/icon-quest-daily.png" 
                alt="Quest"
                className="w-12 h-12 object-contain"
                style={{ filter: canUseQuestGenerator ? 'drop-shadow(0 0 4px rgba(168, 85, 247, 0.6))' : 'brightness(0.5)' }}
              />
              <div>
                <h3 className={`text-lg font-orbitron font-bold transition-colors ${
                  canUseQuestGenerator
                    ? 'text-white'
                    : 'text-gray-500'
                }`}>
                  {t.aiSelector.questGenerator.title}
                </h3>
                <p className="text-xs font-rajdhani text-purple-400">
                  {t.quests.questType}
                </p>
              </div>
            </div>
            <p className={`text-sm font-rajdhani leading-relaxed mb-4 ${
              canUseQuestGenerator ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {t.aiSelector.questGenerator.description}
            </p>
            <div className={`flex items-center gap-2 text-xs font-rajdhani ${
              canUseQuestGenerator ? 'text-purple-300/60' : 'text-gray-500'
            }`}>
              <span className={`w-2 h-2 rounded-full ${
                canUseQuestGenerator ? 'bg-purple-400 shadow-lg shadow-purple-400/40 animate-pulse' : 'bg-gray-600'
              }`}></span>
              {t.aiSelector.questGenerator.useCase}
            </div>
          </button>

          {/* Program Generator Option */}
          <button
            onClick={() => {
              if (isDemoMode) {
                showNotification('🔒 Sign up to unlock AI program generation and save your progress!', 'warning');
                return;
              }
              if (canUseProgramGenerator) {
                onSelectProgramGenerator();
              } else {
                const limitCheck = subscriptionService.canUseAIProgramGeneration(profile!);
                showNotification(
                  limitCheck.reason || 'You\'ve reached your AI program generation limit',
                  'warning'
                );
              }
            }}
            disabled={!canUseProgramGenerator}
            className={`group relative overflow-hidden bg-gradient-to-b from-cyan-950/40 via-slate-950/30 to-cyan-950/40 backdrop-blur-sm border-t-2 border-l-2 border-cyan-500/40 border-r-2 border-b-2 border-r-cyan-700/60 border-b-cyan-700/60 rounded-3xl p-6 text-left transition-all duration-300 ${
              canUseProgramGenerator
                ? 'hover:scale-[1.02] cursor-pointer hover:shadow-lg hover:shadow-cyan-500/30'
                : 'opacity-60 cursor-not-allowed'
            }`}
            style={{
              boxShadow: canUseProgramGenerator ? '0 4px 12px rgba(34, 211, 238, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)' : '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
            }}
          >
            {!canUseProgramGenerator && (
              <div className="absolute top-2 right-2 bg-red-500/90 text-white px-3 py-1 rounded-lg text-xs font-bold font-orbitron z-10">
                🔒 LIMIT REACHED
              </div>
            )}
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="/icons/icon-quest-main.png" 
                alt="Program"
                className="w-12 h-12 object-contain"
                style={{ filter: canUseProgramGenerator ? 'drop-shadow(0 0 4px rgba(34, 211, 238, 0.6))' : 'brightness(0.5)' }}
              />
              <div>
                <h3 className={`text-lg font-orbitron font-bold transition-colors ${
                  canUseProgramGenerator
                    ? 'text-white'
                    : 'text-gray-500'
                }`}>
                  {t.aiSelector.programGenerator.title}
                </h3>
                <p className="text-xs font-rajdhani text-cyan-400">
                  {t.programs.activePrograms}
                </p>
              </div>
            </div>
            <p className={`text-sm font-rajdhani leading-relaxed mb-4 ${
              canUseProgramGenerator ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {t.aiSelector.programGenerator.description}
            </p>
            <div className={`flex items-center gap-2 text-xs font-rajdhani ${
              canUseProgramGenerator ? 'text-cyan-300/60' : 'text-gray-500'
            }`}>
              <span className={`w-2 h-2 rounded-full ${
                canUseProgramGenerator ? 'bg-cyan-400 shadow-lg shadow-cyan-400/40 animate-pulse' : 'bg-gray-600'
              }`}></span>
              {t.aiSelector.programGenerator.useCase}
            </div>
          </button>
        </div>

        {/* Footer */}
        {t.aiSelector.footer && (
          <div className="border-t border-slate-600/30 p-4 sm:p-6 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm"
            style={{
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05)'
            }}
          >
            <p className="text-sm text-gray-400 font-rajdhani text-center">
              {t.aiSelector.footer}
            </p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default AIGeneratorSelectorModal;

